import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  DefaultButton,
  IconButton,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  Stack,
  Text,
  TextField,
} from '@fluentui/react';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import useDebounce from '../../../components/hooks/useDebounce';
import { ConfirmDialog } from '../../../components/parts';
import AttachmentList from '../../attachments/AttachmentList';
import PurchaseOrderPdfInline from '../../purchase-orders/components/PurchaseOrderPdfInline';
import { customPanelStyles } from '../../../theme';
import { modalContentStyles } from '../../../utils';
import { toastError, toastSuccess } from '../../../utils/toast';
import {
  formatAmountForInput,
  formatBillitMoney,
  getInvoiceAmountExcl,
  getInvoiceAmountIncl,
  getLinkLinkedAmount,
  getPurchaseOrderSuggestedAmount,
  getRemainingInvoiceExcl,
  normalizeBillitInvoice,
  parseLinkAmountInput,
  sumActiveLinkedAmounts,
} from '../billitLinkAmounts';
import {
  billitColors, billitRadius,
} from '../billitDetailUi';
import BillitInvoiceDocumentView from '../BillitInvoiceDocumentView';
import {
  BillitInvoice,
  BillitPurchaseOrderLink,
  ConstructionSite,
  PurchaseOrder,
  getActivePurchaseOrderLinks,
} from '../types';

type Props = {
  invoice: BillitInvoice;
  detailedInvoice: BillitInvoice | null;
  setDetailedInvoice: (invoice: BillitInvoice) => void;
  onLinksChanged?: () => void;
  invoicePdfUrl?: string | null;
  invoicePdfLoading?: boolean;
};

const listItemStyle = (selected: boolean): React.CSSProperties => ({
  padding: '10px 12px',
  border: selected ? `2px solid ${billitColors.selectedBorder}` : `1px solid ${billitColors.borderLight}`,
  borderRadius: billitRadius.card,
  cursor: 'pointer',
  background: selected ? billitColors.selectedBg : '#fff',
});

const poPickerItemTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
  color: billitColors.text,
  lineHeight: 1.25,
  minWidth: 0,
};

const poPickerItemAmountStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 12,
  color: billitColors.text,
  lineHeight: 1.25,
  flexShrink: 0,
  marginLeft: 8,
  textAlign: 'right',
};

const poPickerItemSupplierStyle: React.CSSProperties = {
  fontSize: 12,
  color: billitColors.textMuted,
  lineHeight: 1.35,
};

const poPickerItemMetaBlockStyle: React.CSSProperties = {
  marginTop: 2,
  paddingTop: 8,
  borderTop: `1px solid ${billitColors.borderLight}`,
};

const poPickerItemMetaLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 10,
  fontWeight: 600,
  color: billitColors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 3,
};

const poPickerItemDescriptionStyle: React.CSSProperties = {
  fontSize: 11,
  color: billitColors.text,
  lineHeight: 1.45,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
};

const formatSiteLocation = (cs: ConstructionSite): string | null => {
  const parts = [
    cs.zip_code != null ? String(cs.zip_code) : null,
    cs.city?.trim() || null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
};

const formatSiteManagerName = (cs: ConstructionSite): string | null => {
  if (!cs.site_manager) return null;
  const name = `${cs.site_manager.first_name} ${cs.site_manager.last_name}`.trim();
  return name || null;
};

const compactButtonStyles = { root: { minHeight: 28, height: 28, padding: '0 8px' } };

const linkPanelFooterHeaderStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 12,
  color: '#323130',
};

const linkPanelFooterCardStyle: React.CSSProperties = {
  borderRadius: billitRadius.card,
  border: `1px solid ${billitColors.borderLight}`,
  background: '#fff',
  minHeight: 132,
};

const linkPanelFooterSectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: billitColors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 6,
  display: 'block',
};

const linkPanelAmountFieldStyles = {
  root: { width: '100%', maxWidth: 140 },
  fieldGroup: { height: 32, minHeight: 32 },
  field: { fontSize: 13 },
};

const linkPanelPresetBarStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 8,
  padding: 4,
  background: billitColors.surface,
  borderRadius: billitRadius.card,
  border: `1px solid ${billitColors.borderLight}`,
};

const linkPanelAmountSummaryStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 8,
  marginTop: 10,
  padding: '8px 10px',
  background: billitColors.surface,
  borderRadius: billitRadius.card,
};

const linkPanelAmountStatStyle: React.CSSProperties = {
  fontSize: 11,
  color: billitColors.textMuted,
  lineHeight: 1.35,
};

const linkPanelAmountStatValueStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: billitColors.text,
  marginTop: 2,
};

const linkPanelPreviewHeight = 'min(72vh, 720px)';

const linkPanelColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
};

const linkPanelPoPlaceholderStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px dashed #c8c6c4',
  borderRadius: billitRadius.card,
  background: '#fff',
  padding: 24,
};

const BillitPurchaseOrderLinks = ({
  invoice,
  detailedInvoice,
  setDetailedInvoice,
  onLinksChanged,
  invoicePdfUrl = null,
  invoicePdfLoading = false,
}: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const invoiceData = detailedInvoice || invoice;

  const [showPOModal, setShowPOModal] = useState(false);
  const [poStep, setPoStep] = useState<1 | 2>(1);
  const [constructionSiteSearch, setConstructionSiteSearch] = useState('');
  const [constructionSites, setConstructionSites] = useState<ConstructionSite[]>([]);
  const [selectedConstructionSite, setSelectedConstructionSite] = useState<ConstructionSite | null>(null);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [poSearchResults, setPoSearchResults] = useState<PurchaseOrder[]>([]);
  const [poSearch, setPoSearch] = useState('');
  const [selectedPOId, setSelectedPOId] = useState<number | null>(null);
  const [assigningPO, setAssigningPO] = useState(false);
  const [linkToUnlink, setLinkToUnlink] = useState<BillitPurchaseOrderLink | null>(null);
  const [removingPO, setRemovingPO] = useState(false);
  const [marksInvoiceFullyLinked, setMarksInvoiceFullyLinked] = useState(true);
  const [marksPoFullyLinked, setMarksPoFullyLinked] = useState(true);
  const [linkComment, setLinkComment] = useState('');
  const [linkAmount, setLinkAmount] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [expandedPoPreviewId, setExpandedPoPreviewId] = useState<number | null>(null);

  const debouncedConstructionSiteSearch = useDebounce(constructionSiteSearch, 300);
  const debouncedPOSearch = useDebounce(poSearch, 300);

  const activeLinks = useMemo(
    () => getActivePurchaseOrderLinks(invoiceData),
    [invoiceData],
  );

  const linkHistory = useMemo(
    () => [...(invoiceData.purchase_order_links || [])].sort(
      (a, b) => new Date(a.linked_at).getTime() - new Date(b.linked_at).getTime(),
    ),
    [invoiceData.purchase_order_links],
  );

  const historyOnly = linkHistory.filter(l => l.unlinked_at);
  const selectedPO = purchaseOrders.find(po => po.id === selectedPOId);
  const canAddLink = !invoiceData.is_fully_linked;
  const invoiceCurrency = invoiceData.currency || 'EUR';
  const invoiceTotalExcl = getInvoiceAmountExcl(invoiceData);
  const invoiceTotalIncl = getInvoiceAmountIncl(invoiceData);
  const linkedTotalExcl = sumActiveLinkedAmounts(activeLinks);
  const remainingExcl = getRemainingInvoiceExcl(invoiceData, activeLinks);
  const selectedPoTotal = getPurchaseOrderSuggestedAmount(selectedPO);

  useEffect(() => {
    if (!selectedPOId) {
      setLinkAmount('');
      return;
    }
    const po = purchaseOrders.find(p => p.id === selectedPOId);
    setLinkAmount(formatAmountForInput(getPurchaseOrderSuggestedAmount(po)));
  }, [selectedPOId]);

  useEffect(() => {
    if (!selectedPOId) return;
    const po = purchaseOrders.find(p => p.id === selectedPOId);
    if (!po) return;
    setLinkAmount(prev => {
      if (prev.trim()) return prev;
      return formatAmountForInput(getPurchaseOrderSuggestedAmount(po));
    });
  }, [purchaseOrders, selectedPOId]);

  const reloadInvoice = async () => {
    const token = await getAccessTokenSilently();
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    if (response.ok) {
      setDetailedInvoice(normalizeBillitInvoice(await response.json()));
      onLinksChanged?.();
    }
  };

  useEffect(() => {
    if (!showPOModal) return;
    setPoStep(1);
    setSelectedConstructionSite(null);
    setSelectedPOId(null);
    setPoSearch('');
    setConstructionSiteSearch('');
    setPoSearchResults([]);
    setMarksInvoiceFullyLinked(true);
    setMarksPoFullyLinked(true);
    setLinkComment('');
    setLinkAmount('');
  }, [showPOModal]);

  useEffect(() => {
    const fetchSites = async () => {
      if (!showPOModal || poStep !== 1) return;
      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({ limit: '20' });
        if (debouncedConstructionSiteSearch.trim()) {
          params.set('search', debouncedConstructionSiteSearch.trim());
        }
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/billit/construction-sites?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.ok) setConstructionSites(await response.json());
      } catch {
        setConstructionSites([]);
      }
    };
    fetchSites();
  }, [showPOModal, poStep, debouncedConstructionSiteSearch, getAccessTokenSilently]);

  useEffect(() => {
    const fetchPoByNumber = async () => {
      if (!showPOModal || poStep !== 1) return;
      const term = debouncedConstructionSiteSearch.trim();
      if (!term) {
        setPoSearchResults([]);
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({
          limit: '20',
          search: term,
          excludeFullyLinked: 'true',
          excludeActiveLinkToInvoiceId: String(invoiceData.id),
        });
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/billit/purchase-orders?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPoSearchResults(response.ok ? await response.json() : []);
      } catch {
        setPoSearchResults([]);
      }
    };
    fetchPoByNumber();
  }, [
    showPOModal,
    poStep,
    debouncedConstructionSiteSearch,
    invoiceData.id,
    getAccessTokenSilently,
  ]);

  useEffect(() => {
    const fetchPOs = async () => {
      if (!showPOModal || poStep !== 2 || !selectedConstructionSite) return;
      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({
          limit: '100',
          construction_site_id: String(selectedConstructionSite.id),
          excludeFullyLinked: 'true',
          excludeActiveLinkToInvoiceId: String(invoiceData.id),
        });
        if (debouncedPOSearch.trim()) params.set('search', debouncedPOSearch.trim());
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/billit/purchase-orders?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPurchaseOrders(response.ok ? await response.json() : []);
      } catch {
        setPurchaseOrders([]);
      }
    };
    fetchPOs();
  }, [showPOModal, poStep, selectedConstructionSite, debouncedPOSearch, invoiceData.id, getAccessTokenSilently]);

  const selectPurchaseOrderFromSearch = (po: PurchaseOrder) => {
    const site = po.construction_site
      ? {
          id: po.construction_site.id,
          name: po.construction_site.name,
          address: po.construction_site.address,
          city: po.construction_site.city,
          zip_code: po.construction_site.zip_code,
          site_manager: po.construction_site.site_manager ?? null,
        }
      : null;

    if (site) {
      setSelectedConstructionSite(site);
    }
    setPurchaseOrders([po]);
    setSelectedPOId(po.id);
    setPoSearch(po.purchase_order_no || '');
    setPoStep(2);
  };

  const handleLink = async () => {
    if (!selectedPOId) return;
    const parsedAmount = parseLinkAmountInput(linkAmount);
    try {
      setAssigningPO(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/purchase-order-links`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            purchase_order_id: selectedPOId,
            marks_invoice_fully_linked: marksInvoiceFullyLinked,
            marks_purchase_order_fully_linked: marksPoFullyLinked,
            comment: linkComment.trim() || undefined,
            linked_amount: parsedAmount,
          }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const updated = normalizeBillitInvoice(await response.json());
      setDetailedInvoice(updated);
      onLinksChanged?.();
      setShowPOModal(false);
      toastSuccess(
        parsedAmount != null
          ? `Bestelbon gekoppeld (${formatBillitMoney(parsedAmount, invoiceCurrency)})`
          : 'Bestelbon gekoppeld',
      );
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Koppelen mislukt');
    } finally {
      setAssigningPO(false);
    }
  };

  const confirmUnlink = async () => {
    if (!linkToUnlink) return;
    try {
      setRemovingPO(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/purchase-order-links/${linkToUnlink.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setLinkToUnlink(null);
      setExpandedPoPreviewId(null);
      toastSuccess('Bestelbon ontkoppeld');
      await reloadInvoice();
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Ontkoppelen mislukt');
    } finally {
      setRemovingPO(false);
    }
  };

  const renderHistoryItem = (link: BillitPurchaseOrderLink) => {
    const isActive = !link.unlinked_at;
    const po = link.purchase_order;
    return (
      <div
        key={`${link.id}-hist`}
        style={{
          padding: '6px 8px',
          fontSize: 12,
          borderBottom: '1px solid #f3f2f1',
          opacity: isActive ? 1 : 0.75,
        }}
      >
        <Text style={{ fontWeight: 600 }}>
          {isActive ? 'Gekoppeld' : 'Ontkoppeld'} — {po?.purchase_order_no || `#${link.purchase_order_id}`}
          {getLinkLinkedAmount(link) != null && (
            <>
              {' '}
              <span style={{ fontWeight: 600, color: billitColors.selectedBorder }}>
                ({formatBillitMoney(getLinkLinkedAmount(link), invoiceCurrency)})
              </span>
            </>
          )}
          {' '}
          <span style={{ fontWeight: 400, color: '#605e5c' }}>
            {moment(isActive ? link.linked_at : link.unlinked_at).format('DD/MM/YY HH:mm')}
          </span>
        </Text>
      </div>
    );
  };

  return (
    <>
      <Stack tokens={{ childrenGap: 10 }}>
        {invoiceData.is_fully_linked && (
          <MessageBar messageBarType={MessageBarType.info}>
            Factuur volledig gekoppeld — geen extra bestelbonnen mogelijk.
          </MessageBar>
        )}

        {(activeLinks.length > 0 || invoiceTotalExcl != null) && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: billitRadius.card,
              border: `1px solid ${billitColors.border}`,
              background: '#faf9f8',
            }}
          >
            <Text
              style={{
                fontWeight: 600,
                fontSize: 12,
                color: '#323130',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Gekoppelde bedragen
            </Text>
            <Stack horizontal wrap tokens={{ childrenGap: 12 }}>
              <Text style={{ fontSize: 12, color: '#323130' }}>
                Factuur (excl.):{' '}
                <strong>{formatBillitMoney(invoiceTotalExcl, invoiceCurrency)}</strong>
              </Text>
              <Text style={{ fontSize: 12, color: '#323130' }}>
                Totaal gekoppeld:{' '}
                <strong>{formatBillitMoney(linkedTotalExcl, invoiceCurrency)}</strong>
              </Text>
              {remainingExcl != null && (
                <Text style={{ fontSize: 12, color: '#323130' }}>
                  Resterend:{' '}
                  <strong>{formatBillitMoney(remainingExcl, invoiceCurrency)}</strong>
                </Text>
              )}
            </Stack>
          </div>
        )}

        {activeLinks.length === 0 && (
          <Text style={{ fontSize: 13, color: '#605e5c' }}>Nog geen bestelbon gekoppeld.</Text>
        )}

        {activeLinks.map(link => {
          const po = link.purchase_order;
          if (!po) return null;
          const previewOpen = expandedPoPreviewId === po.id;
          const linkedAmount = getLinkLinkedAmount(link);
          return (
            <div
              key={link.id}
              style={{
                border: '1px solid #edebe9',
                borderRadius: billitRadius.card,
                overflow: 'hidden',
              }}
            >
              <Stack
                horizontal
                horizontalAlign='space-between'
                verticalAlign='center'
                styles={{ root: { padding: '8px 10px', background: '#faf9f8' } }}
              >
                <Stack tokens={{ childrenGap: 2 }} grow>
                  <Text style={{ fontWeight: 600, fontSize: 13 }}>
                    {po.purchase_order_no || `#${po.id}`}
                  </Text>
                  <Text style={{ fontSize: 11, color: '#605e5c' }}>
                    {[
                      po.supplier?.name,
                      po.construction_site?.name,
                    ].filter(Boolean).join(' · ')}
                  </Text>
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: linkedAmount != null ? billitColors.selectedBorder : '#8a8886',
                    }}
                  >
                    Gekoppeld bedrag:{' '}
                    {linkedAmount != null
                      ? formatBillitMoney(linkedAmount, invoiceCurrency)
                      : 'niet ingevuld'}
                  </Text>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                  <IconButton
                    iconProps={{ iconName: previewOpen ? 'ChevronUp' : 'PDF' }}
                    title={previewOpen ? 'PDF verbergen' : 'Bestelbon PDF'}
                    onClick={() => setExpandedPoPreviewId(previewOpen ? null : po.id)}
                  />
                  <IconButton
                    iconProps={{ iconName: 'RemoveLink' }}
                    title='Ontkoppelen'
                    disabled={removingPO}
                    onClick={() => setLinkToUnlink(link)}
                  />
                </Stack>
              </Stack>
              {previewOpen && (
                <div style={{ padding: 8 }}>
                  <Stack tokens={{ childrenGap: 10 }}>
                    <PurchaseOrderPdfInline purchaseOrderId={po.id} height={320} />
                    <AttachmentList
                      title='Bestelbon bijlagen'
                      endpoint={`/purchase-orders/${po.id}/attachments`}
                      blobPrefix={`purchase-orders/${po.id}`}
                      canUpload={false}
                      canDelete={false}
                    />
                  </Stack>
                </div>
              )}
            </div>
          );
        })}

        {canAddLink && (
          <DefaultButton
            onClick={() => setShowPOModal(true)}
            iconProps={{ iconName: 'Link' }}
            styles={{ root: { alignSelf: 'flex-start' } }}
          >
            Bestelbon koppelen
          </DefaultButton>
        )}

        {historyOnly.length > 0 && (
          <Stack tokens={{ childrenGap: 4 }}>
            <DefaultButton
              styles={{ root: { alignSelf: 'flex-start', minHeight: 28, height: 28 } }}
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? 'Historiek verbergen' : `Historiek (${historyOnly.length})`}
            </DefaultButton>
            {showHistory && historyOnly.map(renderHistoryItem)}
          </Stack>
        )}
      </Stack>

      {showPOModal && (
        <Panel
          isLightDismiss
          isOpen={showPOModal}
          onDismiss={() => setShowPOModal(false)}
          closeButtonAriaLabel='Sluiten'
          headerText='Bestelbon koppelen'
          type={PanelType.custom}
          customWidth='min(1720px, 99vw)'
          styles={{
            ...customPanelStyles,
            main: { maxWidth: '99vw' },
            scrollableContent: { overflowY: 'auto' },
            content: { paddingBottom: 0 },
          }}
        >
          {poStep === 1 ? (
            <div
              style={{
                display: 'flex',
                height: linkPanelPreviewHeight,
                minHeight: 480,
                borderTop: '1px solid #edebe9',
              }}
            >
              <div
                style={{
                  ...linkPanelColumnStyle,
                  flex: '1 1 55%',
                  padding: 12,
                  borderRight: '1px solid #edebe9',
                  background: '#faf9f8',
                }}
              >
                <BillitInvoiceDocumentView
                  title='Factuur (referentie)'
                  pdfUrl={invoicePdfUrl}
                  pdfLoading={invoicePdfLoading}
                  ublXmlContent={invoiceData.ubl_xml_content}
                  fillContainer
                  fileName={`factuur-${invoiceData.order_number || invoice.billit_order_id}.pdf`}
                />
              </div>
              <div
                style={{
                  ...linkPanelColumnStyle,
                  flex: '0 0 380px',
                  padding: 12,
                  overflowY: 'auto',
                  background: '#fff',
                }}
              >
                <Stack tokens={{ childrenGap: 10 }}>
                  <Text style={{ fontWeight: 600 }}>1. Selecteer werf of bestelbon</Text>
                  <TextField
                    placeholder='Zoek werf of bestelbonnummer...'
                    value={constructionSiteSearch}
                    onChange={(_, v) => setConstructionSiteSearch(v || '')}
                  />
                  {poSearchResults.length > 0 && (
                    <Stack tokens={{ childrenGap: 6 }}>
                      <Text style={{ fontSize: 11, fontWeight: 600, color: billitColors.textMuted }}>
                        Bestelbonnen
                      </Text>
                      {poSearchResults.map(po => {
                        const internalDescription = po.description?.trim();
                        return (
                          <div
                            key={`po-search-${po.id}`}
                            role='button'
                            tabIndex={0}
                            style={listItemStyle(false)}
                            onClick={() => selectPurchaseOrderFromSearch(po)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                selectPurchaseOrderFromSearch(po);
                              }
                            }}
                          >
                            <Stack tokens={{ childrenGap: 4 }}>
                              <Stack
                                horizontal
                                horizontalAlign='space-between'
                                verticalAlign='start'
                                styles={{ root: { width: '100%' } }}
                              >
                                <Text style={poPickerItemTitleStyle} block>
                                  {po.purchase_order_no || `#${po.id}`}
                                </Text>
                                {po.total_excl != null && (
                                  <Text style={poPickerItemAmountStyle} block>
                                    {formatBillitMoney(po.total_excl, invoiceCurrency)}
                                  </Text>
                                )}
                              </Stack>
                              {po.construction_site?.name && (
                                <Text style={poPickerItemSupplierStyle} block>
                                  {po.construction_site.name}
                                </Text>
                              )}
                              {po.supplier?.name && (
                                <Text style={poPickerItemSupplierStyle} block>
                                  {po.supplier.name}
                                </Text>
                              )}
                              {internalDescription && (
                                <div style={poPickerItemMetaBlockStyle}>
                                  <span style={poPickerItemMetaLabelStyle}>
                                    Omschrijving (intern)
                                  </span>
                                  <Text
                                    style={poPickerItemDescriptionStyle}
                                    block
                                    title={internalDescription}
                                  >
                                    {internalDescription}
                                  </Text>
                                </div>
                              )}
                            </Stack>
                          </div>
                        );
                      })}
                    </Stack>
                  )}
                  <Stack tokens={{ childrenGap: 6 }}>
                    {(poSearchResults.length > 0 || constructionSiteSearch.trim()) && (
                      <Text style={{ fontSize: 11, fontWeight: 600, color: billitColors.textMuted }}>
                        Werven
                      </Text>
                    )}
                    {constructionSites.length === 0 && constructionSiteSearch.trim() ? (
                      <Text style={{ color: '#605e5c', fontSize: 12 }}>
                        Geen werven gevonden.
                      </Text>
                    ) : (
                      constructionSites.map(cs => {
                      const location = formatSiteLocation(cs);
                      const siteManager = formatSiteManagerName(cs);
                      const street = cs.address?.trim() || null;

                      return (
                        <div
                          key={cs.id}
                          role='button'
                          tabIndex={0}
                          style={listItemStyle(false)}
                          onClick={() => {
                            setSelectedConstructionSite(cs);
                            setSelectedPOId(null);
                            setPoSearch('');
                            setPoStep(2);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              setSelectedConstructionSite(cs);
                              setSelectedPOId(null);
                              setPoSearch('');
                              setPoStep(2);
                            }
                          }}
                        >
                          <Stack tokens={{ childrenGap: 4 }}>
                            <Stack
                              horizontal
                              horizontalAlign='space-between'
                              verticalAlign='start'
                              styles={{ root: { width: '100%' } }}
                            >
                              <Text style={poPickerItemTitleStyle} block>
                                {cs.name}
                              </Text>
                              {location && (
                                <Text style={poPickerItemAmountStyle} block>
                                  {location}
                                </Text>
                              )}
                            </Stack>
                            {street && (
                              <Text style={poPickerItemSupplierStyle} block>
                                {street}
                              </Text>
                            )}
                            {siteManager && (
                              <div style={poPickerItemMetaBlockStyle}>
                                <span style={poPickerItemMetaLabelStyle}>
                                  Werfleider
                                </span>
                                <Text style={poPickerItemDescriptionStyle} block>
                                  {siteManager}
                                </Text>
                              </div>
                            )}
                          </Stack>
                        </div>
                      );
                    })
                    )}
                  </Stack>
                </Stack>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                height: linkPanelPreviewHeight,
                minHeight: 520,
                borderTop: '1px solid #edebe9',
              }}
            >
              <div
                style={{
                  ...linkPanelColumnStyle,
                  flex: '1 1 0',
                  padding: 12,
                  borderRight: '1px solid #edebe9',
                  background: '#faf9f8',
                }}
              >
                <BillitInvoiceDocumentView
                  title='Factuur (referentie)'
                  pdfUrl={invoicePdfUrl}
                  pdfLoading={invoicePdfLoading}
                  ublXmlContent={invoiceData.ubl_xml_content}
                  fillContainer
                  fileName={`factuur-${invoiceData.order_number || invoice.billit_order_id}.pdf`}
                />
              </div>
              <div
                style={{
                  ...linkPanelColumnStyle,
                  flex: '0 0 272px',
                  padding: 12,
                  borderRight: '1px solid #edebe9',
                  overflowY: 'auto',
                  background: '#fff',
                }}
              >
                <Stack tokens={{ childrenGap: 10 }}>
                  <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                    <Text style={{ fontWeight: 600, fontSize: 13 }}>2. Bestelbon</Text>
                    <DefaultButton
                      onClick={() => {
                        setPoStep(1);
                        setSelectedPOId(null);
                      }}
                      styles={compactButtonStyles}
                    >
                      Werf
                    </DefaultButton>
                  </Stack>
                  <Text style={{ fontSize: 11, color: '#605e5c' }}>
                    {selectedConstructionSite?.name}
                  </Text>
                  <TextField
                    placeholder='Zoek...'
                    value={poSearch}
                    onChange={(_, v) => setPoSearch(v || '')}
                  />
                  <Stack tokens={{ childrenGap: 6 }}>
                    {purchaseOrders.length === 0 ? (
                      <Text style={{ color: '#605e5c', fontSize: 12 }}>
                        Geen open bestelbonnen op deze werf.
                      </Text>
                    ) : (
                      purchaseOrders.map(po => {
                        const internalDescription = po.description?.trim();
                        const isSelected = selectedPOId === po.id;

                        return (
                          <div
                            key={po.id}
                            role='button'
                            tabIndex={0}
                            style={listItemStyle(isSelected)}
                            onClick={() => setSelectedPOId(po.id)}
                            onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') setSelectedPOId(po.id);
                          }}
                          >
                            <Stack tokens={{ childrenGap: 4 }}>
                              <Stack
                                horizontal
                                horizontalAlign='space-between'
                                verticalAlign='start'
                                styles={{ root: { width: '100%' } }}
                              >
                                <Text style={poPickerItemTitleStyle} block>
                                  {po.purchase_order_no || `#${po.id}`}
                                </Text>
                                {po.total_excl != null && (
                                <Text style={poPickerItemAmountStyle} block>
                                  {formatBillitMoney(po.total_excl, invoiceCurrency)}
                                </Text>
                              )}
                              </Stack>
                              {po.supplier?.name && (
                              <Text style={poPickerItemSupplierStyle} block>
                                {po.supplier.name}
                              </Text>
                            )}
                              {internalDescription && (
                              <div style={poPickerItemMetaBlockStyle}>
                                <span style={poPickerItemMetaLabelStyle}>
                                  Omschrijving (intern)
                                </span>
                                <Text
                                  style={poPickerItemDescriptionStyle}
                                  block
                                  title={internalDescription}
                                >
                                  {internalDescription}
                                </Text>
                              </div>
                            )}
                            </Stack>
                          </div>
                        );
                      })
                    )}
                  </Stack>
                </Stack>
              </div>
              <div
                style={{
                  ...linkPanelColumnStyle,
                  flex: '1 1 0',
                  padding: 12,
                  background: '#faf9f8',
                }}
              >
                <Text style={{ fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
                  {selectedPO
                    ? `Bestelbon — ${selectedPO.purchase_order_no || `#${selectedPO.id}`}`
                    : 'Bestelbon PDF'}
                </Text>
                <div
                  style={{
                    flex: 1,
                    minHeight: 400,
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  {selectedPOId ? (
                    <PurchaseOrderPdfInline
                      key={selectedPOId}
                      purchaseOrderId={selectedPOId}
                      fillContainer
                    />
                  ) : (
                    <div style={linkPanelPoPlaceholderStyle}>
                      <Text style={{ color: '#605e5c', fontSize: 13, textAlign: 'center' }}>
                        Selecteer een bestelbon in het midden om de PDF hier te bekijken.
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {poStep === 2 && (
            <div
              style={{
                borderTop: '1px solid #edebe9',
                background: '#faf9f8',
                padding: '12px 16px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: selectedPOId ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr',
                  gap: 16,
                  alignItems: 'start',
                }}
              >
                <Stack tokens={{ childrenGap: 6 }} styles={{ root: { minWidth: 0 } }}>
                  <Text style={linkPanelFooterHeaderStyle}>Opties bij koppelen</Text>
                  <div style={{ ...linkPanelFooterCardStyle, padding: '12px 14px' }}>
                    <Stack tokens={{ childrenGap: 12 }}>
                      <div>
                        <span style={linkPanelFooterSectionLabelStyle}>Gekoppeld bedrag</span>
                        <TextField
                          ariaLabel={`Bedrag ${invoiceCurrency}`}
                          value={linkAmount}
                          onChange={(_, v) => setLinkAmount(v || '')}
                          placeholder='0.00'
                          suffix={invoiceCurrency}
                          styles={linkPanelAmountFieldStyles}
                        />
                        <div style={linkPanelPresetBarStyle}>
                          <DefaultButton
                            text='Factuur excl.'
                            title='Volledig factuur (excl.)'
                            disabled={invoiceTotalExcl == null}
                            onClick={() => setLinkAmount(formatAmountForInput(invoiceTotalExcl))}
                            styles={compactButtonStyles}
                          />
                          <DefaultButton
                            text='Factuur incl.'
                            title='Volledig factuur (incl.)'
                            disabled={invoiceTotalIncl == null}
                            onClick={() => setLinkAmount(formatAmountForInput(invoiceTotalIncl))}
                            styles={compactButtonStyles}
                          />
                          <DefaultButton
                            text='Bestelbon'
                            title='Bestelbon totaal'
                            disabled={selectedPoTotal == null}
                            onClick={() => setLinkAmount(formatAmountForInput(selectedPoTotal))}
                            styles={compactButtonStyles}
                          />
                          <DefaultButton
                            text='Resterend'
                            title='Resterend (excl.)'
                            disabled={remainingExcl == null}
                            onClick={() => setLinkAmount(formatAmountForInput(remainingExcl))}
                            styles={compactButtonStyles}
                          />
                        </div>
                        <div style={linkPanelAmountSummaryStyle}>
                          <div>
                            <span style={linkPanelAmountStatStyle}>Factuur (excl.)</span>
                            <span style={linkPanelAmountStatValueStyle}>
                              {formatBillitMoney(invoiceTotalExcl, invoiceCurrency)}
                            </span>
                          </div>
                          <div>
                            <span style={linkPanelAmountStatStyle}>Al gekoppeld</span>
                            <span style={linkPanelAmountStatValueStyle}>
                              {formatBillitMoney(linkedTotalExcl, invoiceCurrency)}
                            </span>
                          </div>
                          <div>
                            <span style={linkPanelAmountStatStyle}>Resterend</span>
                            <span style={linkPanelAmountStatValueStyle}>
                              {formatBillitMoney(remainingExcl, invoiceCurrency)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{ borderTop: `1px solid ${billitColors.borderLight}` }} />
                      <div>
                        <span style={linkPanelFooterSectionLabelStyle}>Koppeling</span>
                        <Stack tokens={{ childrenGap: 6 }}>
                          <Checkbox
                            label='Factuur volledig gekoppeld'
                            checked={marksInvoiceFullyLinked}
                            onChange={(_, c) => setMarksInvoiceFullyLinked(!!c)}
                            styles={{
                              root: {
                                margin: 0,
                                padding: '6px 8px',
                                borderRadius: billitRadius.card,
                                background: marksInvoiceFullyLinked
                                  ? billitColors.selectedBg
                                  : billitColors.surface,
                                border: `1px solid ${
                                  marksInvoiceFullyLinked
                                    ? billitColors.selectedBorder
                                    : billitColors.borderLight
                                }`,
                              },
                              label: { fontSize: 12 },
                            }}
                          />
                          <Checkbox
                            label='Bestelbon volledig gekoppeld'
                            checked={marksPoFullyLinked}
                            onChange={(_, c) => setMarksPoFullyLinked(!!c)}
                            styles={{
                              root: {
                                margin: 0,
                                padding: '6px 8px',
                                borderRadius: billitRadius.card,
                                background: marksPoFullyLinked
                                  ? billitColors.selectedBg
                                  : billitColors.surface,
                                border: `1px solid ${
                                  marksPoFullyLinked
                                    ? billitColors.selectedBorder
                                    : billitColors.borderLight
                                }`,
                              },
                              label: { fontSize: 12 },
                            }}
                          />
                          <TextField
                            placeholder='Opmerking (optioneel)'
                            value={linkComment}
                            onChange={(_, v) => setLinkComment(v || '')}
                            styles={{
                              root: { width: '100%' },
                              fieldGroup: { height: 32, minHeight: 32 },
                              field: { fontSize: 13 },
                            }}
                          />
                        </Stack>
                      </div>
                    </Stack>
                  </div>
                </Stack>
                {selectedPOId != null && (
                  <Stack tokens={{ childrenGap: 6 }} styles={{ root: { minWidth: 0 } }}>
                    <Text style={linkPanelFooterHeaderStyle}>Bestelbon bijlagen</Text>
                    <div
                      style={{
                        ...linkPanelFooterCardStyle,
                        maxHeight: 132,
                        overflowY: 'auto',
                        padding: 0,
                      }}
                    >
                      <AttachmentList
                        title='Bestelbon bijlagen'
                        hideHeader
                        endpoint={`/purchase-orders/${selectedPOId}/attachments`}
                        blobPrefix={`purchase-orders/${selectedPOId}`}
                        canUpload={false}
                        canDelete={false}
                        refreshKey={selectedPOId}
                      />
                    </div>
                  </Stack>
                )}
              </div>
              <Stack
                horizontal
                horizontalAlign='end'
                verticalAlign='center'
                tokens={{ childrenGap: 8 }}
                styles={{
                  root: {
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid #edebe9',
                  },
                }}
              >
                <DefaultButton onClick={() => setShowPOModal(false)} styles={compactButtonStyles}>
                  Annuleren
                </DefaultButton>
                <PrimaryButton
                  onClick={handleLink}
                  disabled={!selectedPOId || assigningPO}
                  iconProps={{ iconName: 'Link' }}
                  styles={compactButtonStyles}
                >
                  Koppelen
                </PrimaryButton>
              </Stack>
            </div>
          )}
          {poStep === 1 && (
          <Stack
            horizontal
            horizontalAlign='end'
            verticalAlign='center'
            tokens={{ childrenGap: 8 }}
            className={modalContentStyles.footer}
            styles={{
              root: {
                padding: '8px 16px 12px',
                borderTop: '1px solid #edebe9',
                background: '#fff',
              },
            }}
          >
            <DefaultButton onClick={() => setShowPOModal(false)} styles={compactButtonStyles}>
              Annuleren
            </DefaultButton>
          </Stack>
          )}
        </Panel>
      )}

      <ConfirmDialog
        hidden={!linkToUnlink}
        onDismiss={() => setLinkToUnlink(null)}
        onConfirm={confirmUnlink}
        title='Bestelbon ontkoppelen'
        subText={
          linkToUnlink
            ? `Bestelbon "${linkToUnlink.purchase_order?.purchase_order_no || linkToUnlink.purchase_order_id}" ontkoppelen? Gekopieerde bijlagen worden verwijderd.`
            : undefined
        }
        confirmText='Ontkoppelen'
        confirmDisabled={removingPO}
      />
    </>
  );
};

export default BillitPurchaseOrderLinks;
